import { useState, useCallback } from 'react';
import * as ProjectTypeService from '../../../services/ProjectTypeService';
import { FieldConfig } from '../../../services/ValidationService';
import { logDebug, logError, LogTag } from '../../../utils/utils';
import { GroupedFields } from '../../../types/FormTypes';

/**
 * Hook for managing form field configuration
 * Single responsibility: Form field structure and configuration
 */
export const useFormConfiguration = () => {
    const [groupedFields, setGroupedFields] = useState<GroupedFields>({});
    const [accordionTitles, setAccordionTitles] = useState<Record<string, string>>({});
    const [configError, setConfigError] = useState<string>('');

    const loadFormConfiguration = useCallback(async (type: string, method: string, hasBeenRan = false, projectInfoData?: any) => {
        try {
            logDebug('FORM_CONFIG', `Loading configuration for ${type}/${method}`, undefined, LogTag.FORM_SETUP);

            const projectTypeJson = await ProjectTypeService.getProjectTypeJson({ type, method });
            if (!projectTypeJson?.fields) {
                throw new Error(`Config JSON invalid for ${type}/${method}`);
            }

            // Filter out design sections if project hasn't been run yet
            const filteredFields = { ...projectTypeJson.fields };

            // Hide design sections logic
            const isPavedProject = type === 'paved' || type === 'airport';

            // Check if there's existing design data
            const hasExistingDesignData =
                projectInfoData &&
                ((projectInfoData as any).surfaceDesign?.enableSurfaceDesign === 'yes' ||
                    (projectInfoData as any).baseDesign?.enableBaseDesign === 'yes' ||
                    (projectInfoData as any).subbaseDesign?.enableSubbaseDesign === 'yes');

            if (!hasBeenRan && !isPavedProject) {
                logDebug('FORM_CONFIG', 'Hiding design sections for non-paved project', undefined, LogTag.FORM_SETUP);
                delete filteredFields.surfaceDesign;
                delete filteredFields.baseDesign;
                delete filteredFields.subbaseDesign;
            } else if (!hasBeenRan && isPavedProject && !hasExistingDesignData) {
                // For paved projects without existing design data, also hide the sections
                logDebug('FORM_CONFIG', 'Paved project has not been run yet and no existing design data. Hiding design sections', undefined, LogTag.FORM_SETUP);
                delete filteredFields.surfaceDesign;
                delete filteredFields.baseDesign;
                delete filteredFields.subbaseDesign;
            } else if (!hasBeenRan && isPavedProject && hasExistingDesignData) {
                // For paved projects with existing design data, keep the sections
                logDebug('FORM_CONFIG', 'Paved project has existing design data. Keeping design sections visible even though hasBeenRan is false', undefined, LogTag.FORM_SETUP);
            }

            setGroupedFields(filteredFields);
            setAccordionTitles(projectTypeJson.accordionTitles || {});
            setConfigError('');

            // Return flat fields for further processing
            const flatFields = Object.values(filteredFields).flat() as FieldConfig[];
            return {
                fields: filteredFields,
                flatFields: flatFields.filter(f => f.name && f.type),
                projectTypeJson,
            };
        } catch (error) {
            logError('FORM_CONFIG', 'Error loading form configuration', error, LogTag.FORM_SETUP);
            setConfigError('error_loading_configuration');
            throw error;
        }
    }, []);

    const updateDesignSections = useCallback((show: boolean) => {
        setGroupedFields(prev => {
            if (show) {
                // Logic to show design sections would go here
                // This would need the original projectTypeJson
                return prev;
            } else {
                const updated = { ...prev };
                delete updated.surfaceDesign;
                delete updated.baseDesign;
                delete updated.subbaseDesign;
                return updated;
            }
        });
    }, []);

    const updateGroupedFieldsForDesignChange = useCallback(
        async (
            formValues: any,
            projectType?: string,
            projectMethod?: string,
            projectUnit?: string,
            setupFormForMethodCallback?: (method: string, type: string, unit: any, data: any) => Promise<Record<string, any>>
        ) => {
            const isPaved = projectType === 'paved' || projectType === 'airport';

            if (!isPaved || !formValues) return;

            // Apply mutual exclusion logic to ensure only one design feature is enabled
            const currentSurfaceEnabled = formValues?.surfaceDesign?.enableSurfaceDesign === 'yes';
            const currentBaseEnabled = formValues?.baseDesign?.enableBaseDesign === 'yes';
            const currentSubbaseEnabled = formValues?.subbaseDesign?.enableSubbaseDesign === 'yes';

            // Count how many design features are enabled
            const enabledCount = [currentSurfaceEnabled, currentBaseEnabled, currentSubbaseEnabled].filter(Boolean).length;

            // If any design is enabled
            const hasAnyDesignEnabled = enabledCount > 0;

            // Check if we need to show/hide design sections based on current state
            const hasSurfaceSection = 'surfaceDesign' in groupedFields;
            const hasBaseSection = 'baseDesign' in groupedFields;
            const hasSubbaseSection = 'subbaseDesign' in groupedFields;

            const shouldHaveDesignSections = hasAnyDesignEnabled;
            const currentlyHasDesignSections = hasSurfaceSection || hasBaseSection || hasSubbaseSection;

            logDebug(
                'FORM_CONFIG',
                'updateGroupedFieldsForDesignChange check',
                {
                    shouldHaveDesignSections,
                    currentlyHasDesignSections,
                    willCallSetup: shouldHaveDesignSections && !currentlyHasDesignSections,
                    hasCallback: !!setupFormForMethodCallback,
                    groupedFieldsKeys: Object.keys(groupedFields),
                },
                LogTag.FORM_SETUP
            );

            // If the sections visibility doesn't match what it should be, update groupedFields
            if (shouldHaveDesignSections && !currentlyHasDesignSections && setupFormForMethodCallback) {
                logDebug(
                    'FORM_CONFIG',
                    'Calling setupFormForMethod to show design sections',
                    {
                        method: projectMethod || 'aashto',
                        projectType,
                        unit: projectUnit || 'SI',
                    },
                    LogTag.FORM_SETUP
                );

                // Re-trigger form setup to include design sections
                const method = projectMethod || 'aashto';
                const unit = projectUnit || 'SI';
                await setupFormForMethodCallback(method, projectType, unit as any, formValues);
            }
        },
        [groupedFields]
    );

    return {
        groupedFields,
        accordionTitles,
        configError,
        loadFormConfiguration,
        updateDesignSections,
        updateGroupedFieldsForDesignChange,
        setGroupedFields,
        setAccordionTitles,
    };
};

export type UseFormConfigurationReturn = ReturnType<typeof useFormConfiguration>;
